import {Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy, OnInit} from '@angular/core';

/** O TypeScript 4.1 do projeto ainda não traz o tipo do ResizeObserver, que os navegadores já têm. */
interface SizeObserver {
    observe(target: Element): void;
    disconnect(): void;
}
declare const ResizeObserver: new (callback: () => void) => SizeObserver;

/**
 * Mostra quantas linhas couberem no espaço que sobra para o texto e termina com reticências.
 * Recalcula quando o banner muda de tamanho ou quando troca o aviso.
 */
@Directive({
    selector: '[fitLines]'
})
export class FitLinesDirective implements OnInit, OnChanges, OnDestroy
{
    /** O texto exibido; só serve para recalcular quando o aviso muda. */
    @Input() fitLines: string;

    private _observer: SizeObserver;
    private readonly _onResize = (): void => this._schedule();
    private _frame: number;

    constructor(private _element: ElementRef<HTMLElement>, private _ngZone: NgZone)
    {
    }

    ngOnInit(): void
    {
        const host = this._element.nativeElement;
        host.style.display = '-webkit-box';
        host.style.webkitBoxOrient = 'vertical';
        host.style.overflow = 'hidden';
        host.style.minHeight = '0';

        this._ngZone.runOutsideAngular(() => {
            this._observer = new ResizeObserver(() => this._schedule());
            this._observer.observe(host.parentElement);
            // No celular o bloco só cresce com o texto, então quem avisa que o banner aumentou é a janela
            window.addEventListener('resize', this._onResize);
        });
    }

    ngOnChanges(): void
    {
        this._schedule();
    }

    ngOnDestroy(): void
    {
        this._observer?.disconnect();
        window.removeEventListener('resize', this._onResize);
        cancelAnimationFrame(this._frame);
    }

    private _schedule(): void
    {
        cancelAnimationFrame(this._frame);
        this._frame = requestAnimationFrame(() => this._fit());
    }

    private _fit(): void
    {
        const host = this._element.nativeElement;
        const lineHeight = parseFloat(getComputedStyle(host).lineHeight);
        if (!lineHeight) {
            return;
        }

        // Sem o limite, o flex dá ao texto todo o espaço livre; dali sai quantas linhas cabem
        host.style.webkitLineClamp = 'unset';
        host.style.maxHeight = '';
        const lines = Math.floor((host.clientHeight + 1) / lineHeight);

        // O line-clamp só põe as reticências: se a caixa for mais alta, a linha seguinte aparece pela metade
        host.style.webkitLineClamp = String(Math.max(lines, 1));
        host.style.maxHeight = `${Math.max(lines, 1) * lineHeight}px`;
        host.style.visibility = lines < 1 ? 'hidden' : '';
    }
}
